import { useQueries } from '@tanstack/react-query'
import { useRouter } from 'expo-router'
import type { ReactNode } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { View } from 'react-native'

import { Button } from '@/src/elements/button/default'
import { Typography } from '@/src/elements/text/Typography'
import { useActiveJourneySubscriptionsQuery } from '@/src/features/community/services/useActiveJourneySubscriptionsQuery'
import { useEverlastingSubscriptionsQuery } from '@/src/features/community/services/useEverlastingSubscriptionsQuery'
import { CategoryCard } from '@/src/features/community/ui/components/CategoryCard'
import type { TCommunityCardSliderProps } from '@/src/features/community/ui/components/CommunityCardSlider'
import { CurrentlyCommunitiesSection } from '@/src/features/community/ui/screens/CommunitiesScreen/CurrentlyCommunitiesSection'
import { FeaturedTagSlider } from '@/src/features/community/ui/screens/CommunitiesScreen/FeaturedTagSlider'
import type { TSmallTagApi } from '@/src/features/tag/api/types/tagApi.types'
import { getTagQueryOptions } from '@/src/features/tag/services/useTagQuery'
import { getImageUrl } from '@/src/general/utils/getImageUrl'
import { useRoutes } from '@/src/general/utils/routes'

const DISCOVER_TAG_SLUGS = {
  fr: ['prier-chaque-jour', 'evangile', 'liturgie-des-heures'],
  en: ['pray-each-day', 'daily-gospel'],
  es: ['orar-todos-los-dias', 'evangelio'],
  pt: ['oracoes-cotidianas', 'evangelho'],
  it: [],
  pl: [],
}

export const DiscoverSection = (): ReactNode => {
  const { locale } = useIntl()
  const router = useRouter()
  const { routes } = useRoutes()

  const { data: everlastingSubscriptions = [] } = useEverlastingSubscriptionsQuery()
  const { data: journeySubscriptions = [] } = useActiveJourneySubscriptionsQuery()

  const [eachDayTag, ...headTags] = useQueries({
    queries: DISCOVER_TAG_SLUGS[locale].map((discoverTagSlug) => getTagQueryOptions(discoverTagSlug)),
    combine: (results) =>
      results.reduce<TSmallTagApi[]>((acc, result, index) => {
        const tag = result.data
        const subscriptions = [...everlastingSubscriptions, ...journeySubscriptions]

        if (
          tag &&
          // Keep the first tag (eachDayTag) that will be used to display a slider
          (index === 0 ||
            // For following tags (headTags), filter out those linked to a community the user is already subscribed to
            !subscriptions?.find((subscription) => subscription.community.mainTag?.id === tag.id))
        ) {
          acc.push(tag)
        }

        return acc
      }, []),
  })

  const sliderClasses: TCommunityCardSliderProps['classes'] = {
    titleContainer: 'px-0 pt-0',
    container: 'px-0',
    card: 'max-w-[161px]',
  }

  return (
    <View
      className="bg-white rounded-xl w-full px-4 pt-8 pb-24"
      style={{ boxShadow: '0 -20px 25px -5px rgba(0, 0, 0, 0.10), 0 -10px 10px -5px rgba(0, 0, 0, 0.04)' }}
    >
      <View className="max-w-screen-lg w-full mx-auto lg:px-8 gap-10">
        <Typography heading="h1" black contrast="high" size="xl">
          <FormattedMessage id="prayer-space.discover" />
        </Typography>
        <View className="gap-4 flex-row flex-wrap">
          {headTags.map((tag) => (
            <CategoryCard
              isPressable
              resetSizes
              onPress={() => router.navigate(routes.communitiesCategory(tag.slug))}
              key={tag.id}
              imageSrc={getImageUrl(tag.icon)}
              content={tag.name}
              className="h-40 flex-1 web:shadow-none w-full max-w-screen-sm min-w-60"
              typographyClassName="text-lg"
            />
          ))}
        </View>
        <View>
          <CurrentlyCommunitiesSection classes={sliderClasses} communityCardVariant="prayerSpaceV2" />
          {eachDayTag && (
            <FeaturedTagSlider
              tag={eachDayTag}
              link={routes.communitiesCategory(eachDayTag.slug)}
              classes={sliderClasses}
              communityCardVariant="prayerSpaceV2"
            />
          )}
        </View>
        <Button variant="faded" onPress={() => router.navigate(routes.communities())}>
          <FormattedMessage id="discover.see-more" />
        </Button>
      </View>
    </View>
  )
}
